import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useNavigate } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import { useCreateSeason } from "../../../queries/game-pass/useCreateSeason";
import {
  mapScheduleValuesToUpdateSeasonRequest,
  useUpdateSeason,
} from "../../../queries/game-pass/useUpdateSeason";
import { useScheduleSeason } from "../../../queries/game-pass/useScheduleSeason";
import {
  buildSeasonScheduleConflictMessage,
  findSeasonScheduleConflict,
} from "../../../queries/game-pass/seasonScheduleConflict";
import { useGetGamePassConfig } from "../../../queries/game-pass/useGetGamePassConfig";
import { useGetSeasonsList } from "../../../queries/game-pass/useGetSeasonsList";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { resolveGamePassImgKeyForSave } from "../../../utils/gamePassImageUpload";
import {
  GAME_PASS_DROPDOWN_LABELS,
  SIDER_TABS,
} from "../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { Button } from "../../shared";
import WizardPageHeader from "../shared/WizardPageHeader";
import WizardStepper from "../shared/WizardStepper";

import SeasonOverview from "./SeasonOverview/SeasonOverview";
import ChallengesAndTasks from "./ChallengesAndTasks/ChallengesAndTasks";
import Schedule from "./Schedule/Schedule";
import {
  CHALLENGES_INITIAL_VALUES,
  SCHEDULE_INITIAL_VALUES,
  SEASON_OVERVIEW_INITIAL_VALUES,
  hasAtLeastOneChallenge,
  isPremiumPriceGcValid,
  isScheduleValid,
  isSeasonOverviewValid,
  parsePremiumPrice,
  resolveAutoStartAnchor,
  type ChallengesValues,
  type ScheduleValues,
  type SeasonOverviewValues,
} from "./createSeasonData";

import styles from "./CreateSeason.module.css";

dayjs.extend(utc);

type AppContextValue = {
  setActiveSidebarTab: (tabName: string, dropdownOption?: string | false) => void;
  adminReadOnly?: boolean;
};

type CreateSeasonStepId = "overview" | "challenges" | "schedule";

const CREATE_SEASON_STEPS: ReadonlyArray<{
  id: CreateSeasonStepId;
  label: string;
}> = [
  { id: "overview", label: AppStrings.SEASON_OVERVIEW },
  { id: "challenges", label: AppStrings.CHALLENGES_AND_TASKS },
  { id: "schedule", label: AppStrings.SCHEDULE },
];

const stepOrder = (id: CreateSeasonStepId): number =>
  CREATE_SEASON_STEPS.findIndex((step) => step.id === id);

// TODO(GAMEPASS): remove once the BE lets a draft be created without a schedule.
// The create endpoint requires `start_date`/`end_date` AND enforces end > start
// (422 "end_date must be after start_date"), but a brand-new draft has no real
// window yet — the admin sets that on the Schedule step. We therefore stamp a
// tiny 1-minute placeholder window ~1 month out (never in the past). It's short
// enough that the overlap guard treats it as "unscheduled" and ignores it
// (`findSeasonScheduleConflict` skips sub-hour windows), so half-finished drafts
// can't falsely block other seasons (FE-1693). The admin's real dates overwrite
// this the moment they complete the Schedule step.
const PLACEHOLDER_DRAFT_WINDOW_MINUTES = 1;

const buildTempDraftWindow = (): { start: string; end: string } => {
  const start = dayjs
    .utc()
    .add(1, "month")
    .startOf("month")
    .hour(9)
    .minute(0)
    .second(0)
    .millisecond(0);
  return {
    start: start.toISOString(),
    end: start.add(PLACEHOLDER_DRAFT_WINDOW_MINUTES, "minute").toISOString(),
  };
};

const CreateSeason: FC = () => {
  const navigate = useNavigate();
  const { setActiveSidebarTab, adminReadOnly } = useContext(
    AppContext,
  ) as AppContextValue;
  useGetGamePassConfig();
  const createSeasonMutation = useCreateSeason();
  const updateSeasonMutation = useUpdateSeason();
  const scheduleSeasonMutation = useScheduleSeason();
  // Existing seasons drive the overlap guard on the final Schedule step
  // (FE-1693) — a new season can't be scheduled over an active / scheduled /
  // draft one. Unfiltered fetch so every blocking status is covered in one go.
  const seasonsListQuery = useGetSeasonsList();

  const [activeStep, setActiveStep] = useState<CreateSeasonStepId>("overview");
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const [seasonOverview, setSeasonOverview] = useState<SeasonOverviewValues>(
    SEASON_OVERVIEW_INITIAL_VALUES,
  );
  const [challenges, setChallenges] = useState<ChallengesValues>(
    CHALLENGES_INITIAL_VALUES,
  );
  const [schedule, setSchedule] = useState<ScheduleValues>(
    SCHEDULE_INITIAL_VALUES,
  );
  // Both the footer "Next" and "Save as Draft" CTAs fire the same
  // `createSeasonMutation` on the Overview step, so `isPending` alone
  // can't tell which button to spin. Tracking the originating action
  // keeps the loader on the button the admin actually clicked.
  const [submittingAction, setSubmittingAction] = useState<
    "next" | "draft" | null
  >(null);

  useEffect(() => {
    setActiveSidebarTab(
      SIDER_TABS?.GAME_PASS,
      GAME_PASS_DROPDOWN_LABELS.SEASONS,
    );
  }, [setActiveSidebarTab]);

  const handleExit = useCallback(() => {
    navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS);
  }, [navigate]);

  const handleHeaderBack = useCallback(() => {
    const idx = stepOrder(activeStep);
    if (idx <= 0) {
      handleExit();
      return;
    }
    setActiveStep(CREATE_SEASON_STEPS[idx - 1].id);
  }, [activeStep, handleExit]);

  const isCurrentStepValid = (() => {
    if (activeStep === "overview") {
      return (
        isSeasonOverviewValid(seasonOverview) &&
        isPremiumPriceGcValid(seasonOverview)
      );
    }
    if (activeStep === "challenges") return hasAtLeastOneChallenge(challenges);
    return isScheduleValid(schedule);
  })();

  // The Schedule step's overlap guard (FE-1693) compares against the full
  // seasons list. If that list is still in flight — or failed to load — when
  // the admin submits, `findSeasonScheduleConflict` would receive an empty
  // array and silently pass, defeating the guard. Block the Next / Schedule
  // CTA until the list has resolved successfully so the check always runs
  // against real data.
  const isScheduleStepBlockedByList =
    activeStep === "schedule" &&
    (seasonsListQuery.isLoading || seasonsListQuery.isError);

  // The active season the new draft auto-starts after — drives the Schedule
  // step's start auto-fill (active end + 1 min) and the `trigger_season_id`
  // link saved on the draft (FE-1970). The draft we just created is excluded so
  // it can never anchor to itself.
  const autoStartAnchor = useMemo(
    () =>
      resolveAutoStartAnchor(seasonsListQuery.data?.seasons ?? [], seasonId),
    [seasonsListQuery.data?.seasons, seasonId],
  );

  const ensureSeasonDraft = useCallback(async (): Promise<string | null> => {
    if (seasonId) return seasonId;
    try {
      // Local dev still creates the draft at the end of Step 1 so Step 2 can
      // attach tasks to a real `season_id`, but the Step 1 schedule inputs are
      // temporarily hidden until that backend/UI contract settles.
      // Tiny placeholder window (end > start so the BE accepts it) that the
      // overlap guard treats as "unscheduled" until the admin sets real dates
      // on the Schedule step.
      const placeholderWindow = buildTempDraftWindow();
      const trimmedDescription = seasonOverview.description.trim();
      let bannerImgKey: string | undefined;
      try {
        const resolved = await resolveGamePassImgKeyForSave({
          file: seasonOverview.imageFile,
          persistedKey: seasonOverview.imgKey,
          folder: "seasons",
        });
        bannerImgKey = resolved ?? undefined;
      } catch (error) {
        message.error(
          getApiErrorMessage(
            error,
            AppStrings.GAME_PASS_SEASON_BANNER_UPLOAD_ERROR,
          ),
        );
        return null;
      }
      const season = await createSeasonMutation.mutateAsync({
        name: seasonOverview.name.trim(),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        start_date: placeholderWindow.start,
        end_date: placeholderWindow.end,
        free_path_enabled: seasonOverview.tiers.free,
        premium_path_enabled: seasonOverview.tiers.premium,
        premium_price_gc: parsePremiumPrice(
          seasonOverview.premiumPriceGc,
          seasonOverview.tiers.premium,
        ),
        premium_price_sc: parsePremiumPrice(
          seasonOverview.premiumPriceSc,
          seasonOverview.tiers.premium,
        ),
        sportsbook_submission_mode:
          seasonOverview.sportsbookSubmissionMode ?? "AUTO",
        casino_submission_mode:
          seasonOverview.casinoSubmissionMode ?? "AUTO",
        ...(bannerImgKey ? { img_key: bannerImgKey } : {}),
      });
      const nextSeasonId = season.id ?? season.season_id ?? null;
      if (!nextSeasonId) {
        throw new Error("Create season response missing id");
      }
      setSeasonId(nextSeasonId);
      setSeasonOverview((current) => ({
        ...current,
        imageFile: undefined,
        imgKey: season.img_key ?? bannerImgKey ?? current.imgKey,
        imageUrl: season.image_url ?? current.imageUrl,
      }));
      return nextSeasonId;
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to create season"));
      return null;
    }
  }, [
    createSeasonMutation,
    seasonId,
    seasonOverview.name,
    seasonOverview.description,
    seasonOverview.tiers.free,
    seasonOverview.tiers.premium,
    seasonOverview.premiumPriceGc,
    seasonOverview.premiumPriceSc,
    seasonOverview.sportsbookSubmissionMode,
    seasonOverview.imageFile,
    seasonOverview.imgKey,
  ]);

  const handleNext = useCallback(async () => {
    if (!isCurrentStepValid) return;
    const idx = stepOrder(activeStep);
    if (activeStep === "overview") {
      setSubmittingAction("next");
      const createdSeasonId = await ensureSeasonDraft();
      setSubmittingAction(null);
      if (!createdSeasonId) return;
    }
    if (idx === CREATE_SEASON_STEPS.length - 1) {
      // Last step (Schedule) — persist the admin's real start/end date+time
      // (or the auto-start trigger season) onto the draft created in Step 1,
      // overwriting the placeholder dates `ensureSeasonDraft` seeded, then
      // schedule the season so it moves out of `draft`.
      if (!seasonId) {
        navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS);
        return;
      }
      // Block dates that overlap an existing active / scheduled season before
      // persisting (FE-1693). The draft we created in Step 1 is excluded via
      // `seasonId` so it never conflicts with itself.
      const conflict = findSeasonScheduleConflict({
        schedule,
        existingSeasons: seasonsListQuery.data?.seasons ?? [],
        excludeSeasonId: seasonId,
      });
      if (conflict) {
        message.error(buildSeasonScheduleConflictMessage(conflict));
        return;
      }
      setSubmittingAction("next");
      try {
        // Save the latest schedule changes onto the draft first, then call the
        // dedicated Schedule Season API to transition it out of `draft`
        // (mirrors `SeasonDetails.handleScheduleSeason`).
        const body = mapScheduleValuesToUpdateSeasonRequest(schedule);
        if (Object.keys(body).length > 0) {
          await updateSeasonMutation.mutateAsync({ seasonId, body });
        }
        await scheduleSeasonMutation.mutateAsync(seasonId);
        navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS);
      } catch (error) {
        message.error(
          getApiErrorMessage(error, "Failed to schedule season"),
        );
      } finally {
        setSubmittingAction(null);
      }
      return;
    }
    setActiveStep(CREATE_SEASON_STEPS[idx + 1].id);
  }, [
    activeStep,
    ensureSeasonDraft,
    isCurrentStepValid,
    navigate,
    schedule,
    scheduleSeasonMutation,
    seasonId,
    seasonsListQuery.data?.seasons,
    updateSeasonMutation,
  ]);

  const handleSaveAsDraft = useCallback(async () => {
    if (activeStep === "overview") {
      if (!isCurrentStepValid) return;
      setSubmittingAction("draft");
      const createdSeasonId = await ensureSeasonDraft();
      setSubmittingAction(null);
      if (!createdSeasonId) return;
      navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS);
      return;
    }

    // On the Schedule step, persist the admin's entered start/end date+time
    // (or the auto-start trigger link) onto the existing draft via the
    // update-season PUT before exiting — so a saved draft keeps the real
    // window instead of the Step 1 placeholder. Unlike Next, this skips the
    // overlap guard: a draft isn't published, so it isn't held to the
    // no-overlap contract until the admin returns to schedule it.
    if (activeStep === "schedule" && seasonId) {
      const body = mapScheduleValuesToUpdateSeasonRequest(schedule);
      if (Object.keys(body).length > 0) {
        setSubmittingAction("draft");
        try {
          await updateSeasonMutation.mutateAsync({ seasonId, body });
        } catch (error) {
          message.error(getApiErrorMessage(error, "Failed to save season"));
          return;
        } finally {
          setSubmittingAction(null);
        }
      }
    }

    navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS);
  }, [
    activeStep,
    ensureSeasonDraft,
    isCurrentStepValid,
    navigate,
    schedule,
    seasonId,
    updateSeasonMutation,
  ]);

  const handleStepClick = useCallback(
    async (target: CreateSeasonStepId) => {
      const currentIdx = stepOrder(activeStep);
      const targetIdx = stepOrder(target);
      // Allow jumping back freely; require the current step to be valid
      // before jumping forward — matches the disabled-Next contract.
      if (targetIdx <= currentIdx) {
        setActiveStep(target);
        return;
      }
      // Only let users advance one step at a time so they can't skip
      // validation on an intermediate step (e.g. jump Overview → Schedule
      // and finish the wizard with zero challenges). Each forward hop must
      // satisfy that step's own Next-button gate.
      if (targetIdx > currentIdx + 1) return;
      // Guard against a double-fire while the create-season POST is in flight:
      // `ensureSeasonDraft` only short-circuits once `seasonId` state is set,
      // which happens after the mutation resolves. Without this, two quick
      // pill clicks would create two draft seasons. Mirrors the disabled-Next
      // contract (`createSeasonMutation.isPending`).
      if (isCurrentStepValid && !createSeasonMutation.isPending) {
        if (activeStep === "overview") {
          const createdSeasonId = await ensureSeasonDraft();
          if (!createdSeasonId) return;
        }
        setActiveStep(target);
      }
    },
    [
      activeStep,
      createSeasonMutation.isPending,
      ensureSeasonDraft,
      isCurrentStepValid,
    ],
  );

  const renderActiveStep = (): ReactNode => {
    if (activeStep === "overview") {
      return (
        <>
          <SeasonOverview
            values={seasonOverview}
            onValuesChange={setSeasonOverview}
            // Once the Step 1 draft exists the admin has moved on to
            // Challenges; let them navigate back to review, but lock the
            // fields since `ensureSeasonDraft` won't persist further edits.
            disabled={seasonId !== null}
          />
        </>
      );
    }
    if (activeStep === "challenges") {
      return (
        <ChallengesAndTasks
          values={challenges}
          onChange={setChallenges}
          enabledTiers={seasonOverview.tiers}
          sportsbookSubmissionMode={seasonOverview.sportsbookSubmissionMode}
          casinoSubmissionMode={seasonOverview.casinoSubmissionMode}
          // `seasonId` is captured at the end of Step 1's "Next" via
          // `ensureSeasonDraft`. Forwarding it lets every `ChallengeCard`
          // call `POST /game-pass-admin/v2/admin/tasks` with the right
          // `season_id` when the user submits the Create Task modal.
          seasonId={seasonId}
        />
      );
    }
    return (
      <Schedule
        values={schedule}
        onValuesChange={setSchedule}
        autoStartAnchor={autoStartAnchor}
      />
    );
  };

  // Read-only page guard — the wizard is URL-reachable, so disabling the
  // listing's Create Season button isn't enough. Placed after every hook
  // call so the hook order stays stable if the flag flips mid-session.
  if (adminReadOnly === true) {
    return (
      <>
        <div className={styles.page} data-testid="create-season-read-only">
          <WizardPageHeader
            title={AppStrings.CREATE_SEASON}
            backAriaLabel={AppStrings.CREATE_SEASON_BACK_BUTTON_ARIA}
            onBack={handleExit}
            backTestId="create-season-page-back"
          />
          <main className={styles.wizardCard}>
            <p>{AppStrings.READ_ONLY_PAGE_NOTICE}</p>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.page} data-testid="create-season">
        <WizardPageHeader
          title={AppStrings.CREATE_SEASON}
          backAriaLabel={AppStrings.CREATE_SEASON_BACK_BUTTON_ARIA}
          onBack={handleExit}
          backTestId="create-season-page-back"
        />

        <main className={styles.wizardCard}>
          <div className={styles.wizardHeader}>
            <button
              type="button"
              className={styles.wizardHeaderAction}
              onClick={handleHeaderBack}
              data-testid="create-season-step-back"
            >
              <LeftOutlined className={styles.wizardHeaderActionIcon} />
              <span>{AppStrings.BACK}</span>
            </button>

            <WizardStepper
              steps={CREATE_SEASON_STEPS}
              activeStepId={activeStep}
              ariaLabel={AppStrings.CREATE_SEASON_STEPS_ARIA}
              idPrefix="create-season-step"
              panelIdPrefix="create-season-step-panel"
              onStepClick={handleStepClick}
            />

            <button
              type="button"
              className={styles.wizardHeaderActionRight}
              onClick={handleNext}
              disabled={
                !isCurrentStepValid ||
                createSeasonMutation.isPending ||
                updateSeasonMutation.isPending ||
                scheduleSeasonMutation.isPending ||
                isScheduleStepBlockedByList
              }
              data-testid="create-season-step-next"
            >
              <span>{AppStrings.NEXT}</span>
              <RightOutlined className={styles.wizardHeaderActionIcon} />
            </button>
          </div>

          <div
            className={styles.stepPanel}
            role="tabpanel"
            id={`create-season-step-panel-${activeStep}`}
            aria-labelledby={`create-season-step-${activeStep}`}
            data-testid={`create-season-step-panel-${activeStep}`}
          >
            {renderActiveStep()}
          </div>

          <div className={styles.footer}>
            <Button
              intent="neutral"
              variant="outline"
              onClick={handleSaveAsDraft}
              disabled={
                createSeasonMutation.isPending ||
                updateSeasonMutation.isPending ||
                (activeStep === "overview" && !isCurrentStepValid)
              }
              loading={submittingAction === "draft"}
              data-testid="create-season-save-draft"
            >
              {AppStrings.SAVE_AS_DRAFT}
            </Button>
            <Button
              intent="primary"
              onClick={handleNext}
              disabled={
                !isCurrentStepValid ||
                createSeasonMutation.isPending ||
                updateSeasonMutation.isPending ||
                scheduleSeasonMutation.isPending ||
                isScheduleStepBlockedByList
              }
              loading={submittingAction === "next"}
              data-testid="create-season-footer-next"
            >
              {AppStrings.NEXT}
            </Button>
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateSeason;
